import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TermFilter } from '@dofleini/query-builder';
import { NotificationService } from '../services';
import { NOTIFICATION_USER_KEY, NOTIFICATION_COUNT_KEY, NOTIFICATION_LIST_KEY } from '../constants/query';
import { NotificationTypeEnum } from '../constants/notification.enum';

interface UseDeleteAllNotificationsOptions {
  showToast?: boolean;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export const useDeleteAllNotifications = (options?: UseDeleteAllNotificationsOptions) => {
  const { showToast = true, onSuccess, onError } = options || {};
  const queryClient = useQueryClient();
  const { t } = useTranslation('notification');

  return useMutation({
    mutationFn: (notificationType?: NotificationTypeEnum) => {
      if (!notificationType) {
        return NotificationService.removeAllNotifications();
      }

      const filter = new TermFilter({ field: 'notificationKind', value: notificationType }).toQuery();
      return NotificationService.removeAllNotifications({ filters: filter });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_USER_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_COUNT_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_LIST_KEY] });

      // Clear all notification data
      queryClient.setQueriesData({ queryKey: [NOTIFICATION_USER_KEY] }, (oldData: any) => ({
        ...oldData,
        data: [],
        total: 0,
      }));

      queryClient.setQueriesData({ queryKey: [NOTIFICATION_COUNT_KEY] }, 0);
    },
    onError: (error, context) => {
      if (showToast) {
        toast.error(t('errorDeletingAll'));
      }

      onError?.(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_USER_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_LIST_KEY] });

      onSuccess?.();
    },
  });
};
